<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import FullCalendar from '@fullcalendar/vue3';
import { CalendarOptions, EventClickArg } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin, { DateClickArg } from '@fullcalendar/interaction';
import { modifiedRRulePlugin, useDateTime } from '@/composables';
import { EMIT } from '@/enum';
import { useCalendarStore } from '@/store';

const emit = defineEmits<{
  (eventName: EMIT.DOCUMENT_EVENT, info: EventClickArg): void;
  (eventName: EMIT.EDIT_EVENT, info: EventClickArg): void;
  (eventName: EMIT.NEW_EVENT, info: DateClickArg): void;
  (eventName: EMIT.DAY_VIEW, info: Date): void;
  (eventName: EMIT.MOUNTED, info: string): void;
}>();

const calendar = ref();
const calendarStore = useCalendarStore();

defineExpose({ calendar });

onMounted(() => {
  setCalendarEvents();
});

const setCalendarEvents = () => {
  calendarOptions.value.dayMaxEvents = true;
  calendarOptions.value.events = calendarStore.events || [];
};

watch(() => calendarStore.events, setCalendarEvents);

const calendarOptions = ref<CalendarOptions>({
  plugins: [dayGridPlugin, interactionPlugin, modifiedRRulePlugin, timeGridPlugin],
  initialView: 'dayGridMonth',
  headerToolbar: false,
  dayHeaderFormat: { weekday: 'short' },
  dayMaxEvents: true,
  dayMaxEventRows: true,
  showNonCurrentDates: false,
  views: {
    timeGrid: {
      dayMaxEventRows: 2,
    },
  },
  moreLinkClick: 'popover',
  moreLinkClassNames: 'more-link-popover',
  navLinks: true,
  weekends: true,
  height: '100%',
  events: [],
  eventClick: (info: EventClickArg) => {
    const isDocumented = info.event._def.extendedProps.eventStatus === 'documented';
    const isInPast = (info.event.start as Date) < new Date();
    const isCaseloadManagement = calendarStore.isCaseloadManagement(info.event._def.extendedProps.eventType);
    const showDocumentEvent = isDocumented || isInPast;
    const isCaseloadAndInCurrentMonth =
      isCaseloadManagement && useDateTime().isSameMonthAndYear(info.event.start as Date);

    closeMoreLinkPopover();

    if (showDocumentEvent && !isCaseloadAndInCurrentMonth) {
      emit(EMIT.DOCUMENT_EVENT, info);
    } else {
      emit(EMIT.EDIT_EVENT, info);
    }
  },
  navLinkDayClick: function (date) {
    emit(EMIT.DAY_VIEW, date);
  },
  dateClick: function (info: DateClickArg) {
    emit(EMIT.NEW_EVENT, info);
  },
  viewDidMount: () => emit(EMIT.MOUNTED, 'month'),
});

/**
 * when fullcalendar has 2+ events in creates a popover in MonthView  which needs to close on modal
 */
const closeMoreLinkPopover = () => {
  document.querySelectorAll('.more-link-popover').forEach((link: Element) => {
    if (link.getAttribute('aria-expanded') == 'true') {
      //get controller parent Element
      let parent;
      const control = link.getAttribute('aria-controls');
      if (control) parent = document.getElementById(control);
      if (parent) parent.style.display = 'none';
      //just to be safe reset aria-expanded
      link.setAttribute('aria-expanded', 'false');
    }
  });
};
</script>

<template>
  <div class="calendar-page-content">
    <div class="calendar-page-week-view">
      <FullCalendar ref="calendar" :options="calendarOptions">
        <template #eventContent="arg">
          <div class="fc-event-main-frame">
            <div class="fc-event-time">
              {{ useDateTime().formatTimeByTimezone(arg.event.start, arg.event.extendedProps.hostTimezoneName) }} -
              {{ useDateTime().formatTimeByTimezone(arg.event.end, arg.event.extendedProps.hostTimezoneName) }}
              {{ arg.event.extendedProps.hostTimezone }}
            </div>
            <div class="fc-event-title-container">
              <div class="fc-event-title fc-sticky">
                <div class="inline-block overflow-hidden max-w-12rem text-overflow-ellipsis white-space-nowrap">
                  {{ arg.event.title }}
                </div>
              </div>
            </div>
          </div>
        </template>
      </FullCalendar>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/theme/extensions/calendar';

.calendar-page-week-view {
  width: 100%;
  padding-top: 2px;
  :deep(.fc) {
    &::before {
      left: 3px;
    }

    &.fc-theme-standard .fc-view-harness {
      th {
        text-align: center;
      }
      .fc-scrollgrid-section-body > td {
        border-radius: 1rem;
        overflow: hidden;
      }
    }

    .fc-col-header-cell {
      a {
        font-weight: normal;
      }

      a::before {
        display: none;
      }
    }
  }
}
</style>
